import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { handleStripeCheckoutSuccess } from '../lib/stripeCheckout.ts';
import { useAppSystem } from '../hooks/useAppSystem';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { clearSignupPlanSelection, getSignupPlanSelection } from '../lib/signupPlanSelection';

/** Landing-styled page shell (pure black + cyan glow) shared by all three states. */
function SuccessShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="min-h-screen bg-white flex items-center justify-center px-4 relative overflow-hidden"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}
    >
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.08)] p-8 text-center relative z-10">
        {children}
      </div>
    </div>
  );
}

const SubscriptionSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { reload: reloadAppSystem } = useAppSystem();
  const { selectedBusiness } = useBusiness();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string>('');
  const [addonStatus, setAddonStatus] = useState<'idle' | 'adding' | 'done' | 'failed'>('idle');
  const addonsHandledRef = useRef(false);

  /**
   * Add-ons queued during the extended signup flow can only attach AFTER the
   * platform subscription exists — this is that moment. Best-effort: a failed
   * addon never blocks the success flow (the user can add it from the App
   * Store later).
   */
  const addQueuedSignupAddons = async (): Promise<boolean> => {
    const selection = getSignupPlanSelection();
    if (!selection || addonsHandledRef.current) return false;
    addonsHandledRef.current = true;
    clearSignupPlanSelection();
    const businessId = selectedBusiness?.id;
    if (!selection.addonIds.length || !businessId || !supabase) return false;

    setAddonStatus('adding');
    let anyFailed = false;
    for (const addonId of selection.addonIds) {
      try {
        const { data, error: addonError } = await supabase.functions.invoke('stripe-subscription', {
          body: { action: 'add-addon-app', businessId, addonAppId: addonId },
        });
        if (addonError || !data?.success) {
          console.error('Failed to add signup addon:', addonId, addonError || data);
          anyFailed = true;
        }
      } catch (err) {
        console.error('Failed to add signup addon:', addonId, err);
        anyFailed = true;
      }
    }
    setAddonStatus(anyFailed ? 'failed' : 'done');
    void reloadAppSystem?.();
    return true;
  };

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      setError('No session ID found');
      return;
    }

    const verifySession = async () => {
      try {
        console.log('Verifying Stripe session:', sessionId);
        const result = await handleStripeCheckoutSuccess(sessionId);
        
        if (result.success) {
          console.log('✅ Subscription activated successfully:', result);
          setStatus('success');

          // Attach any add-ons chosen during the extended signup, THEN redirect.
          const hadAddons = await addQueuedSignupAddons();

          // Redirect to main app with full page refresh after a short delay
          setTimeout(() => {
            window.location.href = '/';
          }, hadAddons ? 4000 : 3000);
        } else {
          setStatus('error');
          setError('Failed to verify subscription');
        }
      } catch (err) {
        console.error('Error verifying session:', err);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Failed to verify subscription');
      }
    };

    verifySession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  if (status === 'loading') {
    return (
      <SuccessShell>
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-gray-50 border border-gray-200 rounded-full flex items-center justify-center mb-4">
            <Loader2 className="h-8 w-8 text-gray-900 animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h1>
          <p className="text-gray-600">
            Please wait while we verify your subscription...
          </p>
        </div>
      </SuccessShell>
    );
  }

  if (status === 'error') {
    return (
      <SuccessShell>
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-red-50 border border-red-200 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
          <p className="text-gray-600 mb-4">
            {error}
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => navigate('/admin-subscription-management')}
            className="w-full bg-gray-900 text-white py-3 px-4 rounded-full font-semibold hover:bg-black transition-colors duration-200"
          >
            Return to Subscription Management
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full bg-white border border-gray-200 text-gray-600 py-3 px-4 rounded-full font-semibold hover:bg-gray-50 transition-colors duration-200"
          >
            Go to Dashboard
          </button>
        </div>
      </SuccessShell>
    );
  }

  return (
    <SuccessShell>
      <div className="mb-6">
        <div className="mx-auto w-16 h-16 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="h-8 w-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
        <p className="text-gray-600 mb-4">
          Your subscription has been activated successfully.
        </p>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
        <div className="flex items-start">
          <CheckCircle className="h-5 w-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-left">
            <h3 className="font-semibold text-emerald-800 mb-1">Subscription Active</h3>
            <p className="text-sm text-emerald-700">
              Your team now has full access to all features. You'll be redirected to the dashboard shortly.
            </p>
          </div>
        </div>
      </div>

      {addonStatus === 'adding' && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-gray-900 animate-spin flex-shrink-0" />
          <p className="text-sm text-gray-700 text-left">Adding the add-on apps you picked during signup…</p>
        </div>
      )}
      {addonStatus === 'done' && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
          <p className="text-sm text-gray-700 text-left">Your add-on apps are installed and ready.</p>
        </div>
      )}
      {addonStatus === 'failed' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700 text-left">
            Some add-ons couldn't be added automatically — you can add them anytime from the App Store.
          </p>
        </div>
      )}

      <div className="space-y-3">
        <button
          onClick={() => navigate('/', { replace: true })}
          className="w-full bg-gray-900 text-white py-3 px-4 rounded-full font-semibold hover:bg-black shadow-sm transition-all duration-200"
        >
          Go to Dashboard
        </button>
        
        <button
          onClick={() => navigate('/settings')}
          className="w-full bg-white border border-gray-200 text-gray-600 py-3 px-4 rounded-full font-semibold hover:bg-gray-50 transition-colors duration-200"
        >
          Manage Subscription
        </button>
      </div>
    </SuccessShell>
  );
};

export default SubscriptionSuccess; 